import { z, ZodErrorMap } from 'zod/v3';
import {
  errorMap,
  transformNull,
  transformNullish,
  zCode,
  zCodeWithoutLeadingNumber,
  zDescription,
  zName,
  zValidPeriodWithFromRequired,
} from '../../common/utils';
import {
  DataObjectFormType,
  DataObjectOutputType,
  DataObjectType,
  DataSourceFormDataSchema,
  DimensionValidFormDataSchema,
} from '../types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../translations/DataObjectForm.translations.json';
import { AlternativeAlgorithmSetSchema } from './AlternativeAlgorithmFormSchema';
import {
  ExecutionDeadlineSchema,
  ExecutionValidFormDataSchema,
} from './ExecutionFormDataSchema';
import { RequiredKeyDataSchema } from './RequiredKeyDataSchema';
import { ValidationRuleValidDataSchema } from './ValidationRuleDataValidSchema';

const { addTranslations, t } = i18nNsFactory(
  'data-object-valid-form-data-schema',
);
addTranslations(translations);

export const decimalsErrorMap: ZodErrorMap = (issue) => {
  if (issue.code === z.ZodIssueCode.too_big) {
    return { message: t('validation.indicator.decimals.too_big') };
  }
  return { message: t('validation.indicator.decimals.integer') };
};

const IndicatorValidFormDataSchema = z.object({
  id: z.number().nullish(),
  sequenceNumber: transformNull<number | undefined>(
    z.number(),
    undefined,
  ).optional(),
  code: zCodeWithoutLeadingNumber(),
  name: zName(errorMap(() => t('validation.indicator.name.required'))).min(1),
  description: zDescription().nullable(),
  decimals: z.number({ errorMap: decimalsErrorMap }).int().max(10).nullish(),
  multiplierId: z.number(
    errorMap(() => t('validation.indicator.multiplier.required')),
  ),
  unitId: z.number(errorMap(() => t('validation.indicator.unit.required'))),
  intermediate: transformNull(z.boolean(), false).optional(),
  width: z
    .number(errorMap(() => t('validation.field_required')))
    .positive()
    .max(999),
  imputeFromIndicator: transformNull<string | undefined>(
    z.string(),
    '',
  ).nullish(),
  required: transformNull(z.boolean(), false).optional(),

  periodTypeId: z.any(),
});

export const IndicatorInValidFormDataSchema =
  IndicatorValidFormDataSchema.extend({
    periodTypeId: z.number(
      errorMap(() => t('validation.indicator.periodType.required')),
    ),
  });

export const IndicatorOutValidFormDataSchema =
  IndicatorValidFormDataSchema.extend({
    periodTypeId: z.number(
      errorMap(() => t('validation.indicator.periodType.required')),
    ),
  });

export const IndicatorRepValidFormDataSchema =
  IndicatorValidFormDataSchema.extend({
    periodTypeId: z.number().optional(),
  });

export const IndicatorTempValidFormDataSchema =
  IndicatorValidFormDataSchema.extend({
    periodTypeId: z.number(
      errorMap(() => t('validation.indicator.periodType.required')),
    ),
  });

export const DataObjectBaseValidFormDataSchema = z.object({
  dataObjectType: z.nativeEnum(DataObjectType),
  outputTypeCode: z.nativeEnum(DataObjectOutputType).nullish(),
  versionId: z.number().nullish(),
  dataObjectId: z.number().nullish(),
  statusCode: transformNull<string>(z.string(), ''),
  name: zName(errorMap(() => t('validation.name.required'))).min(1),
  code: zCode(),
  description: zDescription().optional(),
  groupAndSubgroupId: z.string().nullish(),
  domainId: z.number(errorMap(() => t('validation.domain.required'))),
  accessGroupId: z.number(errorMap(() => t('validation.accessGroup.required'))),
  frequencyId: z.number(errorMap(() => t('validation.frequency.required'))),
  externalCode: z.string().optional(),
  responsibleId: z
    .number(errorMap(() => t('validation.responsible.required')))
    .min(1),
  confidential: z.boolean(),
  indicatorStatus: z.boolean(),
  aggregateValues: z.boolean(),
  validPeriod: zValidPeriodWithFromRequired(),
  versionDescription: zDescription().optional(),
  subjectDimension: transformNull(z.boolean(), true),
  dimensions: z.array(DimensionValidFormDataSchema),
  dataSources: z.array(DataSourceFormDataSchema).default([]),
  execution: ExecutionValidFormDataSchema,
  executionDeadlines: z.array(ExecutionDeadlineSchema),
  validationRules: z.array(ValidationRuleValidDataSchema),
  requiredKeys: z.array(RequiredKeyDataSchema),
  alternativeAlgorithmSets: z.array(AlternativeAlgorithmSetSchema).default([]),
});

const DataObjectInBaseValidFormDataSchema =
  DataObjectBaseValidFormDataSchema.extend({
    formType: z.literal(DataObjectFormType.IN),
    dataProviderGroupId: z.number(
      errorMap(() => t('validation.dataProviderGroup.required')),
    ),
    sourceIds: z.array(
      z.number(),
      errorMap(() => t('validation.source.required')),
    ),
    recipientIds: transformNullish<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ),
    confidentialityStatusId: z.number().optional(),
    indicators: z.array(IndicatorInValidFormDataSchema).default([]),
    validationRules: z.array(ValidationRuleValidDataSchema).default([]),
  });

const DataObjectOutBaseValidFormDataSchema =
  DataObjectBaseValidFormDataSchema.extend({
    formType: z.literal(DataObjectFormType.OUTPUT),
    dataProviderGroupId: z.number().nullish(),
    sourceIds: transformNullish<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ),
    recipientIds: transformNullish<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ),
    confidentialityStatusId: z.number().optional(),
    indicators: z.array(IndicatorOutValidFormDataSchema).default([]),
  });

const DataObjectRepBaseValidFormDataSchema =
  DataObjectBaseValidFormDataSchema.extend({
    formType: z.literal(DataObjectFormType.REPORT),
    dataProviderGroupId: z.number().nullish(),
    sourceIds: transformNullish<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ),
    recipientIds: z
      .array(
        z.number(),
        errorMap(() => t('validation.recipients.required')),
      )
      .min(1),
    confidentialityStatusId: z.number(
      errorMap(() => t('validation.confidentialityStatus.required')),
    ),
    indicators: z.array(IndicatorRepValidFormDataSchema).default([]),
  });

const DataObjectTempBaseValidFormDataSchema =
  DataObjectBaseValidFormDataSchema.extend({
    formType: z.literal(DataObjectFormType.INTERMEDIATE),
    outputDataObjectId: z.number(
      errorMap(() => t('validation.outputDataObject.required')),
    ),
    outputDataObjectCode: z.string().nullish(),
    outputDataObjectName: z.string().nullish(),
    dataProviderGroupId: z.number().nullish(),
    sourceIds: transformNullish<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ),
    recipientIds: transformNullish<number[] | undefined>(
      z.array(z.number()),
      undefined,
    ),
    confidentialityStatusId: z.number().optional(),
    indicators: z.array(IndicatorTempValidFormDataSchema).default([]),
  });

export const DataObjectValidFormDataSchema = z.discriminatedUnion('formType', [
  DataObjectInBaseValidFormDataSchema,
  DataObjectOutBaseValidFormDataSchema,
  DataObjectRepBaseValidFormDataSchema,
  DataObjectTempBaseValidFormDataSchema,
]);
